import { DateTime } from 'luxon';

import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  output,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';

import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputOtpModule } from 'primeng/inputotp';
import { PasswordModule } from 'primeng/password';
import { RippleModule } from 'primeng/ripple';

import { NgIconComponent } from '@ng-icons/core';

import { CapsLockComponent } from '@components/caps-lock/caps-lock.component';
import { ErrorSmallComponent } from '@components/error-handling/error-small/error-small.component';

import { ErrorStore, UserStore } from '@cxc/core/services';
import { StoreService } from '@cxc/core/services';
import { IdentityAuthAPI } from '@services/API/identity/auth.service';
import { FormService } from '@services/form.service';
import { NavigationService } from '@services/navigation.service';
import { AppConfigStore } from '@services/store/app-config.store';

import { CustomValidators } from '@cxc/core/utils';

@Component({
  selector: 'change-password-page',
  templateUrl: './change-password.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,

    ButtonModule,
    RippleModule,
    FloatLabelModule,
    IconFieldModule,
    InputIconModule,
    PasswordModule,
    DividerModule,
    InputOtpModule,
    //Toast,

    ErrorSmallComponent,
    CapsLockComponent,

    NgIconComponent,
  ],
})
export class ChangePasswordPage implements OnInit, OnDestroy {
  formService = inject(FormService);
  errorStore = inject(ErrorStore);
  storeService = inject(StoreService);
  private identityAuthAPI = inject(IdentityAuthAPI);

  paswordForm!: FormGroup;

  deadline!: DateTime;
  countdown = signal(60 * 3);
  //epochStore = new Store('Reset-Password-Epoch', '');

  close = output<void>();
  forgetPasswordPhoneNumber = this.identityAuthAPI.forgetPasswordPhoneNumber;

  constructor(
    public navigationService: NavigationService,
    public userStore: UserStore,
    private appConfigStore: AppConfigStore,

    private messageService: MessageService,

    fb: FormBuilder
  ) {
    this.paswordForm = fb.group({
      password: [
        '',
        [CustomValidators.required(), ...CustomValidators.password('password')],
      ],
      confirmPassword: [
        '',
        [
          CustomValidators.required(),
          CustomValidators.matchField('password', 'password'),
        ],
      ],
      twoFA: [
        '',
        [
          CustomValidators.required(),
          CustomValidators.minLength(6, 'verification code'),
        ],
      ],
    });

    this.storeService.add('Reset-Password-Epoch', '');
  }

  ngOnInit(): void {
    this.errorStore.clear();
    this.countdown.set(this.calculateCountdown());
    this.startTimer();
  }

  ngOnDestroy(): void {
    this.storeService.clear('Reset-Password-Epoch');
    this.identityAuthAPI.forgetPasswordPhoneNumber.set(null);
  }

  calculateCountdown() {
    // First init of store
    if (
      this.storeService.get('Reset-Password-Epoch') === undefined ||
      this.countdown() === 0
    ) {
      this.storeService.set('Reset-Password-Epoch', DateTime.now().toISO());
      // 3 minutes till countdown ends
      return 60 * 3;
    }

    const epoch = DateTime.fromISO(
      this.storeService.get('Reset-Password-Epoch')!
    );
    const diff = DateTime.now().diff(epoch).as('seconds');

    const countdown = 60 * 3 - diff;

    if (countdown < 0) {
      return 0;
    }

    return countdown;
  }

  startTimer() {
    this.deadline = DateTime.fromISO(
      this.storeService.get('Reset-Password-Epoch')!
    );
    this.deadline.plus({ minutes: 3 });

    this.paswordForm.enable();

    const interval = setInterval(() => {
      const diff =
        60 * 3 + Math.ceil(this.deadline.diffNow(['seconds']).seconds);
      this.countdown.set(diff);
      if (this.countdown() <= 0) {
        this.paswordForm.disable();
        clearInterval(interval);
      }
    }, 1000);
  }

  resendCode() {
    // Code has either expired or has never been sent
    this.identityAuthAPI
      .forgotPassword({
        email: this.userStore.get().email!,
      })
      .subscribe({
        complete: () => {
          this.storeService.set('Reset-Password-Epoch', DateTime.now().toISO());
          this.countdown.set(this.calculateCountdown());
          this.startTimer();
        },
      });
  }

  submit() {
    if (this.formService.trySubmitForm(this.paswordForm)) {
      return;
    }

    const twoFA = this.paswordForm.value.twoFA;
    this.paswordForm.controls['twoFA'].setValue('');

    this.identityAuthAPI
      .forgotPasswordConfirm({
        email: this.userStore.get().email!,
        new_password: this.paswordForm.value.password,
        confirmation_code: twoFA,
      })
      .subscribe({
        complete: () => {
          if (this.userStore.get().logged) {
            this.close.emit();
            return;
          }

          this.appConfigStore.patch({
            allowRoutes: { 'change-password': false },
          });

          this.messageService.add({
            severity: 'info',
            summary: 'Info',
            detail: $localize`Your password has been updated.`,
            life: 3000,
          });

          this.navigationService.forward('/login');
        },
      });
  }

  clickCancel() {
    if (this.userStore.get().logged) {
      this.close.emit();
      return;
    }

    this.navigationService.back();
  }
}
